import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Modal, Stack, Typography } from '@mui/material'
import { defaultModalStyle, defaultModalZIndex, FIELD_IS_REQUIRED, minButtonWidth } from 'const'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { any, object, TypeOf } from 'zod'
import FormMultipleSelect from '../../form-elements/FormMultipleSelect'
import { Option } from '../../form-elements/FormSelect'

interface Props {
  title: string
  label: string
  errorText?: string
  buttonText: string
  loadingOptions: boolean
  loading: boolean

  value?: Option[]
  options: Option[]
  isOpen: boolean
  handleClose: () => void
  onSubmit: (newValue: Option[]) => void
  onChangeInput?: (input: Option[]) => void
  controls?: React.ReactNode
}

const modalStyle = {
  ...defaultModalStyle,
  width: 420,
}

const ModalContent = (props: Props) => {
  const {
    value,
    handleClose,
    title,
    label,
    errorText = FIELD_IS_REQUIRED,
    buttonText,
    onSubmit,
    options,
    loadingOptions,
    loading,
    controls,
  } = props

  const formSchema = useMemo(() => {
    return object({
      value: any().refine((val) => val ?? false, errorText),
    })
  }, [errorText])

  const formSettings = useMemo(() => {
    return {
      mode: 'onChange' as const,
      resolver: zodResolver(formSchema),
      defaultValues: {
        value: value,
      },
    }
  }, [formSchema, value])

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>(formSettings)
  const { handleSubmit, setValue, resetField } = methods

  useEffect(() => {
    resetField('value')
    setValue('value', value)
  }, [resetField, setValue, value])

  const onSubmitHandler: SubmitHandler<FormType> = useCallback(
    (values: FormType) => {
      onSubmit(values.value)
    },
    [onSubmit],
  )

  return (
    <Box sx={modalStyle}>
      <Typography variant="h5" mb={3}>
        {title}
      </Typography>

      {controls}

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(onSubmitHandler)(e)
          }}
          width="100%"
          noValidate
        >
          <FormMultipleSelect
            name="value"
            options={loadingOptions ? [] : options}
            isLoading={loadingOptions}
            label={label}
            defaultValue={value || []}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button variant="outlined" onClick={handleClose} sx={minButtonWidth}>
              Close
            </Button>
            <LoadingButton loading={loading} variant="contained" type="submit" sx={minButtonWidth}>
              {buttonText}
            </LoadingButton>
          </Stack>
        </Box>
      </FormProvider>
    </Box>
  )
}

const ModalWithMultipleSelect = (props: Props) => {
  const { isOpen } = props

  return (
    <Modal sx={defaultModalZIndex} open={isOpen}>
      <div>{isOpen ? <ModalContent {...props} /> : <Box />}</div>
    </Modal>
  )
}

export default ModalWithMultipleSelect
